import { createUniqueId, For, type JSX } from 'solid-js';
import { type RailRollup, rollupRail } from '../lib/rail-rollup';
import { AppNavHeading } from './AppNavHeading';
import { type AppNavItem, AppNavLink } from './AppNavLink';

// One rail GROUP (the canvas rail, 08 / APP): the heading button over the region
// of text links. PRESENTER — the rail (`RailGroups`) owns the fold state, the
// arriving-opens rule and the one `<nav>` landmark; this paints one section from
// props and holds no fold of its own, so a caller (or a test) can drive `open`.
//
// No native disclosure: the heading is a `<button>` with `aria-expanded` +
// `aria-controls` over an ALWAYS-RENDERED region — `hidden="until-found"` when
// closed, so find-in-page can still land inside it and the `beforematch` event
// reveals. (`AppNavLink`/`AppNavHeading` carry the row and the heading.)

export type { AppNavItem, NavDotStatus } from './AppNavLink';

export function AppNav(props: {
  /** Already translated. */
  readonly heading: string;
  readonly items: readonly AppNavItem[];
  readonly resolve: (href: string) => string;
  /**
   * The `href` of the ONE lit row on the whole rail (`currentRailItem`), or
   * undefined. Stated by the rail rather than matched per link: a per-link prefix
   * match lights Fleet AND Plans on `/fleet/plans`.
   */
  readonly current?: string | undefined;
  readonly open: boolean;
  readonly onToggle: () => void;
  /** Find-in-page (`beforematch`) landed inside the folded region. */
  readonly onReveal: () => void;
  /** Which row keys are work somebody owes — what a folded heading sums. */
  readonly owed?: ReadonlySet<string> | undefined;
}): JSX.Element {
  const headingId = createUniqueId();
  const regionId = createUniqueId();
  // The rollup exists only while FOLDED: an open section's rows speak for
  // themselves, and a heading that kept its summary would say the same thing
  // twice on the same screen.
  const rollup = (): RailRollup | undefined =>
    props.open
      ? undefined
      : rollupRail(
          props.items.map((item) => ({
            key: item.key,
            count: item.count?.(),
            dot: item.dot?.(),
          })),
          props.owed,
        );
  return (
    <div class="app-nav__group">
      <AppNavHeading
        id={headingId}
        heading={props.heading}
        open={props.open}
        controls={regionId}
        rollup={rollup()}
        onToggle={props.onToggle}
      />
      {/* Always in the DOM (find-in-page reaches a folded row); `until-found`
          takes it out of the accessibility tree until the reader gets there. */}
      {/* biome-ignore lint/a11y/useSemanticElements: a disclosure body needs a group region, not a fieldset's form semantics */}
      <div
        id={regionId}
        role="group"
        aria-labelledby={headingId}
        class="app-nav__links"
        hidden={props.open ? undefined : 'until-found'}
        onBeforeMatch={props.onReveal}
      >
        <For each={props.items}>
          {(item) => (
            <AppNavLink item={item} resolve={props.resolve} lit={props.current === item.href} />
          )}
        </For>
      </div>
    </div>
  );
}
